import React, { useEffect, useState } from 'react';

import {
  Box, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Card, CardContent, useMediaQuery, Chip, FormControlLabel, Switch, IconButton, Tooltip, Snackbar
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon, Edit as EditIcon, History as HistoryIcon, Upload as UploadIcon, SmartToy as AIIcon, Delete as DeleteIcon, DataObject as JsonExportIcon } from '@mui/icons-material';
import TrainingForm, { formatTrainingTypeLabel } from '../components/TrainingForm';
import TrainingUploadDialog from '../components/TrainingUploadDialog';
import AITrainingGenerationDialog from '../components/AITrainingGenerationDialog';
import AIBulkTrainingDialog from '../components/AIBulkTrainingDialog';
import RevisionConfirmDialog from '../components/RevisionConfirmDialog';
import TrainingDeleteDialog from '../components/TrainingDeleteDialog';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import TablePagination from '@mui/material/TablePagination';

const Trainings: React.FC = () => {
  const [trainings, setTrainings] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editTraining, setEditTraining] = useState<any | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [aiGenerationDialogOpen, setAiGenerationDialogOpen] = useState(false);
  const [bulkAiDialogOpen, setBulkAiDialogOpen] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [trainingToDelete, setTrainingToDelete] = useState<any | null>(null);
  
  // Revision-related state
  const [showHistoricalVersions, setShowHistoricalVersions] = useState(false);
  const [showArchivedAndInactive, setShowArchivedAndInactive] = useState(false);
  const [trainingStartedStatus, setTrainingStartedStatus] = useState<{ [key: number]: boolean }>({});
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Revision dialog state
  const [revisionDialog, setRevisionDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    loading: false
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user is admin or superuser
  const canDelete = user && (user.role === 'admin' || user.is_superuser);

  const fetchTrainings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('page_size', String(rowsPerPage));
      if (!showArchivedAndInactive) {
        params.append('status', 'active');
      }
      const response = await api.get(`/trainings/?${params.toString()}`);
      const allTrainings = response.data.results || response.data;
      setCount(response.data.count ?? allTrainings.length ?? 0);
      setTrainings(allTrainings);
      
      // Check started status for each training
      const statusChecks = await Promise.all(
        allTrainings.map(async (training: any) => {
          try {
            const statusResponse = await api.get(`/trainings/${training.id}/check_started_status/`);
            return { 
              id: training.id, 
              hasBeenStarted: statusResponse.data.has_been_started 
            };
          } catch (error) {
            console.error(`Error checking started status for training ${training.id}:`, error);
            return { id: training.id, hasBeenStarted: false };
          }
        })
      );
      
      // Convert to object for easy lookup
      const statusMap = statusChecks.reduce((acc, item) => {
        acc[item.id] = item.hasBeenStarted;
        return acc;
      }, {} as { [key: number]: boolean });
      
      setTrainingStartedStatus(statusMap);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch trainings');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainings();
  }, [page, rowsPerPage, showArchivedAndInactive]);

  const handleAdd = () => {
    setEditTraining(null);
    setFormOpen(true);
  };

  const handleEdit = (training: any) => {
    // Navigate to Training Content Management page with the selected training
    navigate('/training-content', { state: { selectedTrainingId: training.id } });
  };
  
  const handleViewContent = (training: any) => {
    // Navigate to Training Content Management page to view content (read-only for started trainings)
    navigate('/training-content', { state: { selectedTrainingId: training.id } });
  };

  const handleExportJson = async (training: any) => {
    if (!canDelete) return;
    try {
      const response = await api.get(`/trainings/${training.id}/export_json/`, {
        responseType: 'blob'
      });
      const cd = response.headers['content-disposition'] as string | undefined;
      let filename = `training-${training.id}.json`;
      if (cd && cd.includes('filename=')) {
        const m = cd.match(/filename="?([^";]+)"?/i);
        if (m?.[1]) filename = m[1];
      }
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/json' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError('Failed to export training JSON');
    }
  };

  const handleDelete = (training: any) => {
    setTrainingToDelete(training);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setTrainingToDelete(null);
  };

  const handleTrainingDeleted = () => {
    fetchTrainings();
    setSuccessMessage('Training deleted successfully');
    setShowSuccessSnackbar(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditTraining(null);
  };

  const showRevisionDialog = (title: string, message: string, onConfirm: () => void) => {
    setRevisionDialog({
      open: true,
      title,
      message,
      onConfirm,
      loading: false
    });
  };

  const handleRevisionConfirm = async () => {
    setRevisionDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await revisionDialog.onConfirm();
      setRevisionDialog(prev => ({ ...prev, open: false, loading: false }));
    } catch (error) {
      setRevisionDialog(prev => ({ ...prev, loading: false }));
      setError('Failed to create training revision');
    }
  };

  const handleRevisionCancel = () => {
    setRevisionDialog(prev => ({ ...prev, open: false, loading: false }));
  };

  const handleFormSubmit = (data: any) => {
    if (editTraining) {
      api.put(`/trainings/${editTraining.id}/`, data)
        .then(() => {
          fetchTrainings();
          setFormOpen(false);
          setEditTraining(null);
        })
        .catch((error: any) => {
          // Check if this is a revision requirement error
          if (error.response?.data?.requires_revision) {
            showRevisionDialog(
              'Training Revision Required',
              'This training has been started by users. Creating a revision will preserve the original version while creating a new editable version.',
              async () => {
                await api.post(`/trainings/${editTraining.id}/create_revision/`);
                fetchTrainings();
                setFormOpen(false);
                setEditTraining(null);
              }
            );
          } else {
            setError('Failed to update training');
          }
        });
    } else {
      api.post('/trainings/', data)
        .then(() => {
          fetchTrainings();
          setFormOpen(false);
        })
        .catch(() => setError('Failed to create training'));
    }
  };

  const getTrainingTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      'safety': 'error',
      'compliance': 'primary',
      'technical': 'secondary',
      'soft_skills': 'success',
      'certification': 'warning',
      'refresher': 'info'
    };
    return colors[type] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'active': 'success',
      'draft': 'warning',
      'inactive': 'error',
      'archived': 'default'
    };
    return colors[status] || 'default';
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Filter trainings based on version visibility
  const filteredTrainings = trainings.filter(training => {
    const matchesVersion = showHistoricalVersions || training.is_current_version;
    return matchesVersion;
  });

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Trainings</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" color="primary" onClick={handleAdd} startIcon={<AddIcon />}>
            Add Training
          </Button>
          <Button variant="outlined" color="primary" onClick={() => setUploadDialogOpen(true)} startIcon={<UploadIcon />}>
            Upload trainings
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => setAiGenerationDialogOpen(true)} startIcon={<AIIcon />}>
            Generate with AI
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => setBulkAiDialogOpen(true)} startIcon={<AIIcon />}>
            Bulk Generate with AI
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showArchivedAndInactive}
                onChange={(e) => {
                  setShowArchivedAndInactive(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Show archived and inactive"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showHistoricalVersions}
                onChange={(e) => setShowHistoricalVersions(e.target.checked)}
              />
            }
            label="Show historical versions"
          />
        </Box>
      </Box>
      
      {isMobile ? (
        <Box>
          {filteredTrainings.map((training: any) => (
            <Card key={training.id} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {training.title}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Chip 
                    label={formatTrainingTypeLabel(training.training_type)} 
                    color={getTrainingTypeColor(training.training_type) as any}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip 
                    label={training.status?.toUpperCase() || 'N/A'} 
                    color={getStatusColor(training.status) as any}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {training.description || 'No description available'}
                </Typography>
                <Typography variant="body2">
                  Duration: {formatDuration(training.duration_minutes || 0)}
                </Typography>
                <Typography variant="body2">
                  Required: {training.is_required ? 'Yes' : 'No'}
                </Typography>
                {training.is_recurring && (
                  <Typography variant="body2">
                    Recurring: Every {training.recurrence_months} months
                  </Typography>
                )}
                {training.has_quiz && (
                  <Typography variant="body2">
                    Test: {training.passing_score}% passing score
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(training.created_at).toLocaleDateString()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {canDelete && (
                    <Tooltip title="Export JSON">
                      <IconButton
                        size="small"
                        onClick={() => handleExportJson(training)}
                        aria-label="Export training as JSON"
                      >
                        <JsonExportIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {trainingStartedStatus[training.id] ? (
                    <Button variant="outlined" size="small" onClick={() => handleViewContent(training)} startIcon={<HistoryIcon />}>
                      View Content
                    </Button>
                  ) : (
                    <Button variant="outlined" size="small" onClick={() => handleEdit(training)}>
                      Edit
                    </Button>
                  )}
                  {canDelete && (
                    <Button 
                      variant="outlined" 
                      size="small" 
                      color="error"
                      onClick={() => handleDelete(training)}
                      startIcon={<DeleteIcon />}
                    >
                      Delete
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <>
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Title</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Version</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Type</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Duration</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Required</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Recurring</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Test</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' } }}>Passing Score</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' } }}>Created</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTrainings.map((training: any) => (
                <TableRow key={training.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {training.title}
                      </Typography>
                      {training.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {training.description.length > 50 
                            ? `${training.description.substring(0, 50)}...` 
                            : training.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{training.version || 'Rev 1'}</Typography>
                      {training.is_current_version && (
                        <Chip label="Current" size="small" color="primary" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={formatTrainingTypeLabel(training.training_type)} 
                      color={getTrainingTypeColor(training.training_type) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={training.status?.toUpperCase() || 'N/A'} 
                      color={getStatusColor(training.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDuration(training.duration_minutes || 0)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={training.is_required ? 'Yes' : 'No'} 
                      color={training.is_required ? 'success' : 'default' as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {training.is_recurring ? `Every ${training.recurrence_months} months` : 'No'}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Chip 
                      label={training.has_quiz ? 'Yes' : 'No'} 
                      color={training.has_quiz ? 'primary' : 'default' as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                    {training.has_quiz ? `${training.passing_score}%` : 'N/A'}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                    {new Date(training.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      {canDelete && (
                        <Tooltip title="Export JSON">
                          <IconButton
                            size="small"
                            onClick={() => handleExportJson(training)}
                            aria-label="Export training as JSON"
                          >
                            <JsonExportIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {trainingStartedStatus[training.id] ? (
                        // Training has been started - show View Content button
                        <Tooltip title="Training has been started by users. View content to create a revision.">
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => handleViewContent(training)}
                            startIcon={<HistoryIcon />}
                          >
                            View Content
                          </Button>
                        </Tooltip>
                      ) : (
                        // Training not started - show regular Edit button
                        <Tooltip title={training.is_current_version ? "Edit Training" : "Edit Training (Historical Version)"}>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => handleEdit(training)}
                            startIcon={<EditIcon />}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete Training">
                          <Button 
                            variant="outlined" 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(training)}
                            startIcon={<DeleteIcon />}
                          >
                            Delete
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
        </>
      )}
      
      <TrainingForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        initialData={editTraining}
      />
      
      <TrainingUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={() => {
          setUploadDialogOpen(false);
          fetchTrainings();
        }}
      />
      
      <AITrainingGenerationDialog
        open={aiGenerationDialogOpen}
        onClose={() => setAiGenerationDialogOpen(false)}
        onSuccess={() => {
          setAiGenerationDialogOpen(false);
          fetchTrainings();
        }}
      />
      
      <AIBulkTrainingDialog
        open={bulkAiDialogOpen}
        onClose={() => setBulkAiDialogOpen(false)}
        onSuccess={() => {
          setBulkAiDialogOpen(false);
          fetchTrainings();
        }}
      />
      
      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />

      {/* Revision Confirmation Dialog */}
      <RevisionConfirmDialog
        open={revisionDialog.open}
        onClose={handleRevisionCancel}
        onConfirm={handleRevisionConfirm}
        title={revisionDialog.title}
        message={revisionDialog.message}
        loading={revisionDialog.loading}
      />

      {/* Delete Confirmation Dialog */}
      <TrainingDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        training={trainingToDelete}
        onTrainingDeleted={handleTrainingDeleted}
      />
    </Box>
  );
};

export default Trainings; 